import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { AgentTools, type ToolCall, type ToolCallSummary } from '@/features/types/tools';
import { ToolCallContainer } from '../../ToolCallContainer';
import { ToolCallDataList } from '../../ToolCallDataList';
import { useToolCallParser, ToolCallState } from '../../useToolCallParser';
import { ToolCallError } from '../../ToolCallError';
import { MeetingCard } from './common/MeetingCard';
import { ChatMarkDownFormatter } from '../../../ChatMarkDownFormatter';
import { formatSummaryContent } from './common/format-utils';

interface SummarizeTranscriptTool {
  name: AgentTools.chorus_summarize_transcript;
  args: {
    conversationId: string;
    options?: {
      includeSummary?: boolean;
      includeMetrics?: boolean;
      includeParticipants?: boolean;
    };
  };
}

export type SummarizeTranscriptToolCall = ToolCall<
  SummarizeTranscriptTool['name'],
  SummarizeTranscriptTool['args']
>;

export type SummarizeTranscriptToolCallSummary = ToolCallSummary<
  SummarizeTranscriptTool['name'],
  SummarizeTranscriptTool['args']
>;

interface SummarizeTranscriptResult {
  action_items?: string[];
  key_insights?: string[];
  participants?: {
    name: string;
    talkTime: number;
    participationPercentage: number;
  }[];
  summary: string;
  topics?: string[];
}

export function SummarizeTranscriptToolCall({
  toolCall,
}: {
  toolCall: SummarizeTranscriptToolCallSummary;
}) {
  const { t } = useTranslation('prince');

  const { parsedResult, title } = useToolCallParser<SummarizeTranscriptResult>(
    toolCall,
    ({ toolCallState }) => {
      switch (toolCallState) {
        case ToolCallState.Running:
          return t('toolCalls.chorus_summarize_transcript.title.whileRunning');
        case ToolCallState.Error:
          return t('toolCalls.chorus_summarize_transcript.title.errors');
        case ToolCallState.Success:
          return t('toolCalls.chorus_summarize_transcript.title.success');
      }
    }
  );

  const formattedSummary = useMemo(() => {
    if (!parsedResult?.results?.summary) return '';
    return formatSummaryContent(parsedResult.results.summary);
  }, [parsedResult]);

  const tabs = [
    {
      id: 'results',
      label: t('toolCalls.results'),
      showLoadingWhileRunning: true,
      content: parsedResult ? (
        !parsedResult.hasErrors ? (
          <MeetingCard
            participants={(parsedResult.results.participants || []).map(participant => ({
              id: `${participant.talkTime}-${participant.name}`,
              name: participant.name,
            }))}
          >
            <div className="space-y-4">
              {/* Summary Section */}
              {parsedResult.results.summary && (
                <div className="font-normal text-sm leading-relaxed text-gray-700">
                  <ChatMarkDownFormatter content={formattedSummary} />
                </div>
              )}
            </div>
          </MeetingCard>
        ) : (
          <ToolCallError parsedResult={parsedResult} />
        )
      ) : null,
    },
    {
      id: 'parameters',
      label: t('toolCalls.parameters'),
      content: (
        <ToolCallDataList
          items={[
            {
              label: t('toolCalls.chorus.conversationId'),
              value: toolCall.args.conversationId,
            },
          ]}
        />
      ),
    },
  ];

  return (
    <ToolCallContainer
      toolName={toolCall.name}
      title={title}
      isRunning={toolCall.isRunning}
      tabs={tabs}
    />
  );
}
